import { Pressable, View } from 'react-native';

import { Badge, Text } from '@/components/ui';
import {
  formatContextTokens,
  formatPricePer1M,
  retentionSummary,
  type OpenRouterCatalogMetadata,
} from '@/lib/admin/llm-models-types';

type LlmModelCatalogCardProps = {
  metadata: OpenRouterCatalogMetadata;
  providerTag?: string | null;
  onProviderTag?: (tag: string | null) => void;
  compact?: boolean;
};

function policyBits(endpoint: OpenRouterCatalogMetadata['endpoints'][number]): string {
  const train =
    endpoint.dataPolicy.training === true
      ? 'may train on prompts'
      : endpoint.dataPolicy.training === false
        ? 'does not train'
        : 'training policy unknown';
  const retain =
    endpoint.dataPolicy.retainsPrompts === true
      ? 'may retain prompts'
      : endpoint.dataPolicy.retainsPrompts === false
        ? 'does not retain prompts'
        : 'retention policy unknown';
  return `${train} · ${retain}`;
}

export function LlmModelCatalogCard({
  metadata,
  providerTag = null,
  onProviderTag,
  compact,
}: LlmModelCatalogCardProps) {
  const { model, privacy, endpoints } = metadata;
  const zdr = endpoints.filter((endpoint) => endpoint.isZdr);
  const blocked = endpoints.filter((endpoint) => !endpoint.isZdr);
  const pinned = zdr.find((endpoint) => endpoint.tag === providerTag);

  return (
    <View className="rounded-lg border border-border bg-surface-raised p-3">
      <View className="mb-2 flex-row flex-wrap items-center gap-2">
        <Text variant="body" className="font-medium">
          {model.name}
        </Text>
        {privacy.hasZdrEndpoint ? (
          <Badge label="Zero retention available" variant="success" />
        ) : (
          <Badge label="No zero-retention route" variant="danger" />
        )}
      </View>

      <Text variant="caption" className="text-muted">
        {model.id}
      </Text>
      <Text variant="caption" className="mt-2 text-muted">
        {retentionSummary(privacy)}
      </Text>

      {!compact && model.description ? (
        <Text variant="body-sm" className="mt-2 text-muted">
          {model.description}
        </Text>
      ) : null}

      <View className="mt-3 flex-row flex-wrap gap-x-4 gap-y-1">
        <Text variant="caption" className="text-muted">
          Context {formatContextTokens(model.contextLength)}
        </Text>
        <Text variant="caption" className="text-muted">
          In {formatPricePer1M(model.pricing.promptPer1M)} / 1M
        </Text>
        <Text variant="caption" className="text-muted">
          Out {formatPricePer1M(model.pricing.completionPer1M)} / 1M
        </Text>
      </View>

      <Text variant="body-sm" className="mt-4 font-medium">
        Privacy route
      </Text>
      <Text variant="caption" className="mt-1 text-muted">
        {pinned
          ? `Pinned to ${pinned.providerName}. If that provider is down, the call fails instead of using a route that retains data.`
          : 'Any zero-retention route. OpenRouter will not pick a provider that trains on or keeps prompts.'}
      </Text>

      {onProviderTag ? (
        <View className="mt-2 flex flex-col gap-2">
          <Pressable
            className={`rounded-lg border p-3 ${providerTag ? 'border-border' : 'border-accent bg-accent-muted'}`}
            onPress={() => onProviderTag(null)}
          >
            <Text variant="body-sm">Any zero-retention route</Text>
            <Text variant="caption" className="mt-0.5 text-muted">
              Recommended. Still denies data collection.
            </Text>
          </Pressable>
          {zdr.map((endpoint) => {
            const selected = providerTag === endpoint.tag;
            return (
              <Pressable
                key={endpoint.tag}
                className={`rounded-lg border p-3 ${selected ? 'border-accent bg-accent-muted' : 'border-border'}`}
                onPress={() => onProviderTag(endpoint.tag)}
              >
                <Text variant="body-sm">{endpoint.providerName}</Text>
                <Text variant="caption" className="mt-0.5 text-muted">
                  Zero retention · {policyBits(endpoint)} · in{' '}
                  {formatPricePer1M(endpoint.pricing.promptPer1M)} / out{' '}
                  {formatPricePer1M(endpoint.pricing.completionPer1M)} per 1M
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : zdr.length > 0 ? (
        <View className="mt-2 flex flex-col gap-1">
          {zdr.map((endpoint) => (
            <Text key={endpoint.tag} variant="caption" className="text-muted">
              {endpoint.providerName} · zero retention · {policyBits(endpoint)}
            </Text>
          ))}
        </View>
      ) : null}

      {blocked.length > 0 ? (
        <View className="mt-3">
          <Text variant="caption" className="font-medium text-muted">
            Not used
          </Text>
          {blocked.map((endpoint) => (
            <Text key={`${endpoint.tag}-${endpoint.name}`} variant="caption" className="text-muted">
              {endpoint.providerName} · {policyBits(endpoint)}
            </Text>
          ))}
        </View>
      ) : null}

      {privacy.warnings.length > 0 ? (
        <View className="mt-3 rounded border border-warning bg-warning-muted p-2">
          {privacy.warnings.map((warning) => (
            <Text key={warning} variant="caption">
              {warning}
            </Text>
          ))}
        </View>
      ) : null}
    </View>
  );
}
